import { useState, useEffect } from 'react';
import { Modal, useModalClose } from '@/components/ui/Modal';
import type { ButtonHTMLAttributes } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Plus, Search, DollarSign, Calendar, CheckCircle, XCircle,
  RefreshCw, Loader2, FileText, X
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

interface SecurityPayment {
  id: string;
  dealId: string;
  dealNumber: string;
  amount: number;
  paymentDate: string;
  paymentMode: string;
  utrNumber: string;
  status: 'pending' | 'adjusted' | 'returned' | 'forfeited';
  adjustedTo: string | null;
  adjustedAmount: number | null;
  returnedDate: string | null;
  notes: string;
}

export function SecurityPaymentPage() {
  const navigate = useNavigate();
  const [payments, setPayments] = useState<SecurityPayment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');

  const [deals, setDeals] = useState<any[]>([]);
  const [form, setForm] = useState({
    dealId: '',
    amount: '',
    paymentDate: '',
    paymentMode: 'bank_transfer',
    utrNumber: '',
    notes: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [paymentRes, dealsRes] = await Promise.all([
        api.get('/security-payments'),
        api.get('/sales-orders?status=confirmed'),
      ]);
      setPayments(paymentRes.data.data || []);
      setDeals(dealsRes.data.data?.salesOrders || dealsRes.data.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.dealId || !form.amount || !form.paymentDate) {
      alert('Please fill all required fields');
      return;
    }
    setSaving(true);
    try {
      await api.post('/security-payments', form);
      setShowModal(false);
      setForm({ dealId: '', amount: '', paymentDate: '', paymentMode: 'bank_transfer', utrNumber: '', notes: '' });
      fetchData();
    } catch (err) {
      alert('Failed to record payment');
    } finally {
      setSaving(false);
    }
  };

  const handleAdjustment = async (paymentId: string, action: 'adjust' | 'return', amount?: number) => {
    try {
      await api.post(`/security-payments/${paymentId}/${action}`, { amount });
      fetchData();
    } catch (err) {
      alert('Failed to update payment');
    }
  };

  const filteredPayments = payments.filter(p => 
    !search || p.dealNumber.toLowerCase().includes(search.toLowerCase())
  );

  const totalSecurity = payments.reduce((sum, p) => sum + p.amount, 0);
  const adjusted = payments.filter(p => p.status === 'adjusted').reduce((sum, p) => sum + (p.adjustedAmount || 0), 0);
  const returned = payments.filter(p => p.status === 'returned').reduce((sum, p) => sum + p.amount, 0);
  const pending = payments.filter(p => p.status === 'pending').reduce((sum, p) => sum + p.amount, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Security Payments</h1>
          <p className="text-sm text-gray-500">Record and track security deposits to plants</p>
        </div>
        <button onClick={() => setShowModal(true)} className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white">
          <Plus size={16} /> Record Payment
        </button>
      </div>

      {/* Summary */}
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-4">
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><Shield size={16} /> Total Security</div>
          <p className="text-2xl font-bold text-gray-900">{formatINR(totalSecurity)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><RefreshCw size={16} /> Adjusted</div>
          <p className="text-2xl font-bold text-blue-600">{formatINR(adjusted)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><CheckCircle size={16} /> Returned</div>
          <p className="text-2xl font-bold text-green-600">{formatINR(returned)}</p>
        </div>
        <div className="rounded-xl border border-gray-200 bg-white p-4">
          <div className="flex items-center gap-2 text-sm text-gray-500"><DollarSign size={16} /> Pending</div>
          <p className="text-2xl font-bold text-orange-600">{formatINR(pending)}</p>
        </div>
      </div>

      {/* Search */}
      <div className="mb-4 relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" placeholder="Search by deal..." value={search} onChange={e => setSearch(e.target.value)}
          className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-9 pr-4 text-sm" />
      </div>

      {/* Table */}
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50 text-left">
              <th className="px-4 py-3 font-medium text-gray-500">Deal</th>
              <th className="px-4 py-3 font-medium text-gray-500">Amount</th>
              <th className="px-4 py-3 font-medium text-gray-500">Date</th>
              <th className="px-4 py-3 font-medium text-gray-500">UTR</th>
              <th className="px-4 py-3 font-medium text-gray-500">Status</th>
              <th className="px-4 py-3 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredPayments.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-400">No payments recorded</td></tr>
            ) : (
              filteredPayments.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{p.dealNumber}</td>
                  <td className="px-4 py-3 font-medium text-gray-900">{formatINR(p.amount)}</td>
                  <td className="px-4 py-3 text-gray-600">{formatDate(p.paymentDate)}</td>
                  <td className="px-4 py-3 text-gray-600">{p.utrNumber || '-'}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                      p.status === 'pending' ? 'bg-orange-100 text-orange-700' :
                      p.status === 'adjusted' ? 'bg-blue-100 text-blue-700' :
                      p.status === 'returned' ? 'bg-green-100 text-green-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {p.status.charAt(0).toUpperCase() + p.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {p.status === 'pending' && (
                      <div className="flex gap-2">
                        <button onClick={() => {
                          const input = window.prompt(`Adjustment amount (original: ₹${p.amount.toLocaleString('en-IN')})`, String(p.amount));
                          if (input === null) return;
                          const amt = parseFloat(input);
                          if (isNaN(amt) || amt <= 0) { alert('Please enter a valid amount'); return; }
                          handleAdjustment(p.id, 'adjust', amt);
                        }} className="text-xs text-blue-600 hover:underline">Adjust</button>
                        <button onClick={() => handleAdjustment(p.id, 'return')} className="text-xs text-green-600 hover:underline">Return</button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <Modal open onClose={() => setShowModal(false)} ariaLabel="Record Security Payment" className="!max-w-md !bg-white">
            <div className="flex items-center justify-between border-b px-6 py-4">
              <h2 className="text-lg font-semibold">Record Security Payment</h2>
              <RequestCloseButton className="text-gray-400 hover:text-gray-600"><X size={20} /></RequestCloseButton>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium">Deal <span className="text-red-500">*</span></label>
                <select value={form.dealId} onChange={e => setForm({ ...form, dealId: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="">Select deal</option>
                  {deals.map(d => <option key={d.id} value={d.id}>{d.number}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Amount (₹) <span className="text-red-500">*</span></label>
                <input type="number" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="e.g., 500000" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Payment Date <span className="text-red-500">*</span></label>
                <input type="date" value={form.paymentDate} onChange={e => setForm({ ...form, paymentDate: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Payment Mode</label>
                <select value={form.paymentMode} onChange={e => setForm({ ...form, paymentMode: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="bank_transfer">Bank Transfer</option>
                  <option value="upi">UPI</option>
                  <option value="cheque">Cheque</option>
                  <option value="cash">Cash</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">UTR Number</label>
                <input type="text" value={form.utrNumber} onChange={e => setForm({ ...form, utrNumber: e.target.value })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t px-6 py-4">
              <RequestCloseButton className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Cancel</RequestCloseButton>
              <button onClick={handleSubmit} disabled={saving} className="flex items-center gap-2 px-4 py-2 bg-[var(--ink-0)] text-white rounded-lg text-sm disabled:opacity-50">
                {saving ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                Record Payment
              </button>
            </div>
        </Modal>
      )}
    </div>
  );
}

function RequestCloseButton({ children, ...props }: ButtonHTMLAttributes<HTMLButtonElement>) {
  const { requestClose } = useModalClose();
  return <button type="button" {...props} onClick={requestClose}>{children}</button>;
}
